import { useShallow } from 'zustand/react/shallow'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { useEffectiveRoles, useEffectivePrimaryRole } from '@/stores/impersonation'
import { useUIStore } from '@/stores/ui'
import { useSOSStore } from '@/stores/sos'
import { usePanicStore } from '@/stores/panic'
import { useProfilesStore } from '@/stores/profiles'
import { useLocationStore } from '@/stores/location'
import { useMessagingStore } from '@/stores/messaging'
import { ProfileCard } from '@/components/profiles/ProfileCard'
import { USER_ROLES } from '@/constants/roles'
import { ROUTES } from '@/constants/routes'
import { bridgeToLumen, lumenBridgeErrorMessage } from '@/lib/lumenHost'
import { ALL_QUICK_ACTIONS, DEFAULT_QUICK_ACTION_KEYS } from '@/constants/quickActions'
import { prefetchHandlers } from '@/lib/routePrefetch'

import { useDisasterAlerts, getStoredPrefCats, isParRelevantStorm, inPAR, CATEGORY_META, type DisasterRegion } from '@/lib/useDisasterAlerts'
import { useWeather } from '@/lib/useWeather'
import { heatIndexCategory } from '@/lib/useHeatIndex'

const HI_PILL: Record<1 | 2 | 3 | 4, { wrap: string; icon: string }> = {
  1: { wrap: 'bg-yellow-50 border-yellow-300 text-yellow-800',  icon: 'text-yellow-500' },
  2: { wrap: 'bg-orange-50 border-orange-300 text-orange-800',  icon: 'text-orange-500' },
  3: { wrap: 'bg-red-50    border-red-300    text-red-800',     icon: 'text-red-500'    },
  4: { wrap: 'bg-red-100   border-red-500    text-red-900',     icon: 'text-red-600'    },
}

// Categories too slow-moving to warrant a home emergency banner
const BANNER_EXCLUDED_CATS = new Set(['tempExtremes', 'waterColor', 'seaLakeIce'])

// Reuse GPS coords cached by HazardsView so home banner scopes to nearby even without dependent tracking
function getStoredGpsRegion(): DisasterRegion | null {
  try {
    const raw = localStorage.getItem('hazard_gps_coords')
    if (!raw) return null
    const { lat, lng } = JSON.parse(raw) as { lat: number; lng: number }
    return { label: 'your area', lat, lng, radiusKm: 500 }
  } catch { return null }
}

export default function HomeView() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const user = useAuthStore((s) => s.user)
  const primaryRole = useEffectivePrimaryRole()
  const roles = useEffectiveRoles()
  const uid = useAuthStore((s) => s.uid())
  const panicMode = useAuthStore((s) => s.userDoc?.panicMode)
  const savedQuickActionKeys = useAuthStore((s) => s.userDoc?.uiPrefs?.quickActions)

  const guardianViewActive = useUIStore((s) => s.guardianViewActive)
  const showToast = useUIStore((s) => s.showToast)
  const [lumenOpening, setLumenOpening] = useState(false)

  // Do NOT auto-redirect org staff to Lumen. Open via explicit CTA only;
  // bridge establishes Laravel session (or toast + stay here on failure).

  const isOrgUser = roles.some(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)

  // Bounce-back from Lumen form-bridge errors (?lumen_bridge=token|no_org|failed).
  useEffect(() => {
    const code = searchParams.get('lumen_bridge')
    const msg = lumenBridgeErrorMessage(code)
    if (!msg) return
    showToast(msg, 'error')
    const next = new URLSearchParams(searchParams)
    next.delete('lumen_bridge')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, showToast])

  async function openLumenConsole() {
    if (lumenOpening) return
    setLumenOpening(true)
    try {
      const result = await bridgeToLumen('/lumen')
      if (!result.ok) {
        showToast(
          result.reason === 'no_user'
            ? 'Sign in to the family app first, then open Lumen.'
            : 'Could not open Lumen. Stay here and try again.',
          'error',
        )
        setLumenOpening(false)
      }
      // ok → form navigates away
    } catch {
      showToast('Could not open Lumen. Stay here and try again.', 'error')
      setLumenOpening(false)
    }
  }

  const isSOSActive = useSOSStore((s) => s.isSOSActive)
  const isPanicActive = usePanicStore((s) => s.isPanicActive)
  const { profiles, loading, loaded } = useProfilesStore(useShallow((s) => ({ profiles: s.profiles, loading: s.loading, loaded: s.loaded })))

  useEffect(() => {
    if (!isOrgUser && loaded && !loading && profiles.length === 0) {
      navigate(ROUTES.ONBOARDING, { replace: true })
    }
  }, [isOrgUser, loaded, loading, profiles.length, navigate])
  const { fetchSafeZones, listenToAllProfiles, stopListeningToAllProfiles } = useLocationStore(useShallow((s) => ({ fetchSafeZones: s.fetchSafeZones, listenToAllProfiles: s.listenToAllProfiles, stopListeningToAllProfiles: s.stopListeningToAllProfiles })))
  const conversations = useMessagingStore((s) => s.conversations)

  const firstName = user?.displayName?.split(' ')[0] ?? 'there'
  const hasProfiles = profiles.length > 0

  const unreadCount = useMemo(() => {
    if (!uid) return 0
    return conversations.filter((conv) => {
      if (!conv.lastMessageAt) return false
      if (conv.lastSenderUid === uid) return false
      const lastRead = conv.lastReadAt?.[uid]
      if (!lastRead) return true
      return conv.lastMessageAt.toMillis() > lastRead.toMillis()
    }).length
  }, [conversations, uid])

  useEffect(() => {
    if (uid) fetchSafeZones(uid)
    listenToAllProfiles(profiles.map((p) => p.id))
    return () => { stopListeningToAllProfiles() }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    listenToAllProfiles(profiles.map((p) => p.id))
  }, [profiles, listenToAllProfiles])

  const lastSeenMap = useLocationStore((s) => s.lastSeenMap)
  const [hazardBannerDismissed, setHazardBannerDismissed] = useState(false)
  const [heatIndexDismissed, setHeatIndexDismissed] = useState(false)

  const hazardRegion = useMemo((): DisasterRegion | null => {
    const entries = Object.values(lastSeenMap)
    if (entries.length > 0) return { label: 'your area', lat: entries[0].lat, lng: entries[0].lng, radiusKm: 500 }
    // Only fall back to stored GPS if user has profiles (avoids showing heat banner on fresh/0-profile accounts)
    if (hasProfiles) return getStoredGpsRegion()
    return null
  }, [lastSeenMap, hasProfiles])

  const heatWeatherLocs = useMemo(
    () => hazardRegion ? [{ label: hazardRegion.label, lat: hazardRegion.lat, lng: hazardRegion.lng }] : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hazardRegion?.lat, hazardRegion?.lng],
  )
  const { locations: heatWeatherData } = useWeather(heatWeatherLocs)
  const heatCat = heatWeatherData[0] ? heatIndexCategory(heatWeatherData[0].current.feelsLikeC) : null
  const heatFeelsLike = heatWeatherData[0] ? Math.round(heatWeatherData[0].current.feelsLikeC) : null

  const { events: hazardEventsRaw } = useDisasterAlerts({
    region: hazardRegion,
    scope: hazardRegion ? 'nearby' : 'all',
    days: 7,
  })
  const hazardEvents = useMemo(() => {
    const prefCats = getStoredPrefCats()
    return hazardEventsRaw.filter(e =>
      (e.alertLevel === 'red' || e.alertLevel === 'orange') &&
      e.category !== 'severeStorms' &&  // storms surface via the PAR Watch banner
      !BANNER_EXCLUDED_CATS.has(e.category) &&
      (prefCats.length === 0 || prefCats.includes(e.category)),
    )
  }, [hazardEventsRaw])

  // PAR Watch banner: most severe orange/red storm in or approaching PAR.
  // Bypasses prefCats — an incoming typhoon should never be filtered away.
  const [parDismissed, setParDismissed] = useState(false)
  const parStorm = useMemo(() => {
    const storms = hazardEventsRaw
      .filter(e => (e.alertLevel === 'red' || e.alertLevel === 'orange') && isParRelevantStorm(e))
      .sort((a, b) => (a.alertLevel === 'red' ? 0 : 1) - (b.alertLevel === 'red' ? 0 : 1))
    return storms[0] ?? null
  }, [hazardEventsRaw])

  const quickActions = useMemo(() => {
    const isFamilyRole =
      primaryRole === USER_ROLES.FAMILY_ADMIN ||
      primaryRole === USER_ROLES.CO_CARER ||
      primaryRole === USER_ROLES.SUPER_ADMIN
    if (isFamilyRole) {
      const keys = savedQuickActionKeys ?? DEFAULT_QUICK_ACTION_KEYS
      return keys
        .map(k => ALL_QUICK_ACTIONS.find(a => a.key === k))
        .filter((a): a is NonNullable<typeof a> => a != null)
    }
    if (primaryRole === USER_ROLES.FUNDER) {
      return [
        { key: 'messages', icon: 'fa-message',  label: 'Messages', route: ROUTES.MESSAGING },
        { key: 'settings', icon: 'fa-gear',     label: 'Settings', route: ROUTES.SETTINGS },
      ]
    }
    return []
  }, [primaryRole, savedQuickActionKeys])

  return (
    <div className="px-4 pt-3 sm:pt-6 pb-0 space-y-3 sm:space-y-5 max-w-7xl mx-auto">

      {/* Welcome banner */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold text-xl text-slate-900">Hey, {firstName} 👋</p>
          <p className="text-xs text-slate-400 mt-0.5">Your family's safety hub</p>
        </div>
        {(isSOSActive || isPanicActive) && (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 ${
              isSOSActive
                ? 'bg-danger-500 text-white'
                : 'bg-warning-100 text-warning-700 border border-warning-200'
            }`}
          >
            <i className="fa-solid fa-circle text-[8px]" />
            {isSOSActive ? 'SOS Active' : 'Panic Active'}
          </span>
        )}
      </div>

      {/* Org staff: opt-in Lumen via Firebase→Laravel session bridge */}
      {isOrgUser && !guardianViewActive && (
        <button
          type="button"
          disabled={lumenOpening}
          onClick={() => { void openLumenConsole() }}
          className="w-full flex items-center gap-3 rounded-2xl border border-beacon-200 bg-beacon-50 px-4 py-3 text-left hover:bg-beacon-100 transition-colors disabled:opacity-70"
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-beacon-600 text-white">
            <i
              className={`fa-solid ${lumenOpening ? 'fa-circle-notch animate-spin' : 'fa-building-user'}`}
              aria-hidden="true"
            />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-beacon-900">
              {lumenOpening ? 'Opening Lumen…' : 'Open organization console'}
            </span>
            <span className="block text-xs text-beacon-700/80">
              Signs you into Lumen with this account — schedule, clients, team
            </span>
          </span>
          {!lumenOpening && (
            <i className="fa-solid fa-arrow-up-right-from-square text-beacon-500 text-sm flex-shrink-0" aria-hidden="true" />
          )}
        </button>
      )}

      {/* PAR Watch banner — takes the slot over the heat pill while active */}
      {parStorm && !parDismissed && (
        <div className={`rounded-2xl border px-4 py-3 flex items-center gap-3 ${
          parStorm.alertLevel === 'red' ? 'border-danger-300 bg-danger-50' : 'border-warning-300 bg-warning-50'
        }`}>
          <i className={`fa-solid fa-hurricane text-lg flex-shrink-0 ${
            parStorm.alertLevel === 'red' ? 'text-danger-500' : 'text-warning-500'
          }`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold truncate ${
              parStorm.alertLevel === 'red' ? 'text-danger-900' : 'text-warning-900'
            }`}>
              {parStorm.title.replace(/^(super typhoon|typhoon|tropical (cyclone|storm|depression))\s*/i, '')} — {parStorm.point && inPAR(parStorm.point) ? 'In PAR' : 'Approaching PAR'}
            </p>
            <p className={`text-xs ${parStorm.alertLevel === 'red' ? 'text-danger-700' : 'text-warning-700'}`}>
              {parStorm.alertLevel === 'red' ? 'Red alert' : 'Orange alert'} · GDACS
            </p>
          </div>
          <button
            onClick={() => navigate(ROUTES.HAZARDS)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 ${
              parStorm.alertLevel === 'red'
                ? 'text-danger-700 bg-danger-100 hover:bg-danger-200'
                : 'text-warning-700 bg-warning-100 hover:bg-warning-200'
            }`}
          >
            View
          </button>
          <button onClick={() => setParDismissed(true)} className="opacity-50 hover:opacity-80 flex-shrink-0 w-10 h-10 -my-2 -mr-2 flex items-center justify-center">
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
      )}

      {/* Heat index pill — hidden while a PAR storm banner is showing */}
      {!heatIndexDismissed && heatCat && heatFeelsLike !== null && !(parStorm && !parDismissed) && (
        <div className={`rounded-2xl border px-4 py-2.5 flex items-center gap-3 ${HI_PILL[heatCat.level].wrap}`}>
          <i className={`fa-solid fa-temperature-high text-sm flex-shrink-0 ${HI_PILL[heatCat.level].icon}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              Feels like {heatFeelsLike}°C &middot; {heatCat.label}
            </p>
            <p className="text-xs opacity-75 mt-0.5">{heatCat.advisory}</p>
          </div>
          <button
            onClick={() => navigate(ROUTES.WEATHER)}
            className="text-xs font-semibold opacity-80 hover:opacity-100 flex-shrink-0 underline px-2 py-3 -my-3"
          >
            Details
          </button>
          <button onClick={() => setHeatIndexDismissed(true)} className="opacity-50 hover:opacity-80 flex-shrink-0 w-10 h-10 -my-2 -mr-2 flex items-center justify-center">
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
      )}

      {/* Unread messages tile */}
      {unreadCount > 0 && (
        <button
          onClick={() => navigate(ROUTES.MESSAGING)}
          className="w-full rounded-2xl border border-beacon-200 bg-beacon-50 px-4 py-2.5 flex items-center gap-3 active:opacity-80 transition-opacity text-left"
        >
          <i className="fa-solid fa-comments text-sm flex-shrink-0 text-beacon-500" />
          <p className="flex-1 text-sm font-semibold text-beacon-800 leading-tight">
            {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
          </p>
          <i className="fa-solid fa-chevron-right text-beacon-300 text-xs flex-shrink-0" />
        </button>
      )}

      {/* Disaster alerts banner */}
      {hazardEvents.length > 0 && !hazardBannerDismissed && !isSOSActive && !isPanicActive && (
        <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 flex items-center gap-3">
          <i className="fa-solid fa-cloud-bolt text-warning-500 text-lg flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-warning-900">
              {hazardEvents.length} active disaster alert{hazardEvents.length !== 1 ? 's' : ''} nearby
            </p>
            <p className="text-xs text-warning-700 truncate">
              {hazardEvents.slice(0, 2).map(e => CATEGORY_META[e.category]?.label ?? 'Event').join(', ')}
              {hazardEvents.length > 2 ? ` +${hazardEvents.length - 2} more` : ''}
            </p>
          </div>
          <button
            onClick={() => navigate(ROUTES.HAZARDS)}
            className="text-xs font-semibold text-warning-700 bg-warning-100 hover:bg-warning-200 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
          >
            View
          </button>
          <button
            onClick={() => setHazardBannerDismissed(true)}
            className="text-warning-400 hover:text-warning-600 flex-shrink-0"
          >
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
      )}

      {/* Incident active banner */}
      {(isSOSActive || isPanicActive) && (
        <div className={`rounded-2xl p-5 flex items-center gap-4 border-2 ${isSOSActive ? 'bg-danger-50 border-danger-400' : 'bg-warning-50 border-warning-400'}`}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isSOSActive ? 'bg-danger-100' : 'bg-warning-100'}`}>
            <i className={`fa-solid ${isSOSActive ? 'fa-triangle-exclamation text-danger-600' : 'fa-user-secret text-warning-600'} text-xl`} />
          </div>
          <div className="flex-1">
            <p className={`text-base font-medium ${isSOSActive ? 'text-danger-800' : 'text-warning-800'}`}>
              {isSOSActive ? 'SOS Alert Active' : 'Silent Panic Active'}
            </p>
            <p className={`text-sm ${isSOSActive ? 'text-danger-600' : 'text-warning-700'}`}>Open Mission Control for incident status</p>
          </div>
          <div className="flex flex-col gap-1.5">
            {!isSOSActive && panicMode === 'stream' && (
              <button
                onClick={() => navigate(ROUTES.PANIC_WATCH.replace('/:uid?', ''))}
                className="text-sm font-semibold text-white bg-warning-500 hover:bg-warning-600 px-4 py-2 rounded-lg transition-colors"
              >
                Watch Live
              </button>
            )}
            <button
              onClick={() => navigate(ROUTES.MISSION_CONTROL)}
              className={`text-sm font-semibold px-4 py-2 rounded-xl transition-colors ${isSOSActive ? 'text-danger-600 bg-danger-100 hover:bg-danger-200' : 'text-warning-700 bg-warning-100 hover:bg-warning-200'}`}
            >
              Mission Control
            </button>
          </div>
        </div>
      )}

      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 sm:mb-3">Quick Actions</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {quickActions.map((action) => {
              const isMonitor = action.route === ROUTES.MISSION_CONTROL
              const alertForThis = isMonitor && (isSOSActive || isPanicActive)
              return (
                <button
                  key={action.label}
                  {...prefetchHandlers(action.route)}
                  onClick={() => navigate(action.route)}
                  className={[
                    'inline-flex items-center justify-center gap-1.5 px-3 min-h-11 rounded-xl border transition-colors',
                    alertForThis
                      ? isSOSActive
                        ? 'border-danger-400 bg-danger-50 text-danger-600 animate-pulse hover:bg-danger-100'
                        : 'border-warning-400 bg-warning-50 text-warning-600 animate-pulse hover:bg-warning-100'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-beacon-200 hover:bg-beacon-50 hover:text-beacon-700 active:bg-beacon-100',
                  ].join(' ')}
                >
                  <i className={`fa-solid ${action.icon} text-sm sm:text-xs`} />
                  <span className="text-xs font-semibold leading-tight text-center">
                    <span className="sm:hidden">{action.label.split(' ')[0]}</span>
                    <span className="hidden sm:inline">{action.label}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Profiles */}
      <div>
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <p className="text-sm font-bold text-slate-700">
            Safety Profiles
            {hasProfiles && <span className="ml-1.5 text-slate-400 font-normal">({profiles.length})</span>}
          </p>
          <button
            {...prefetchHandlers(ROUTES.PROFILES)}
            onClick={() => navigate(ROUTES.PROFILES)}
            className="flex items-center gap-1 text-xs font-semibold text-beacon-600 hover:text-beacon-700 transition-colors px-2 py-3 -my-3"
          >
            <i className="fa-solid fa-plus text-[10px]" />
            {hasProfiles ? 'View all' : 'Create'}
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-2xl border border-beacon-100 bg-beacon-50 p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-beacon-200 flex-shrink-0" />
                  <div className="flex-1 space-y-2.5">
                    <div className="h-4 bg-beacon-200 rounded-full w-2/3" />
                    <div className="flex gap-2">
                      <div className="h-3 bg-beacon-100 rounded-full w-14" />
                      <div className="h-3 bg-beacon-100 rounded-full w-20" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <div className="h-3 bg-beacon-100 rounded-full w-16" />
                  <div className="h-3 bg-beacon-100 rounded-full w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : hasProfiles ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {profiles.map((profile) => (
              <ProfileCard key={profile.id} profile={profile} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <i className="fa-solid fa-child text-2xl text-slate-400" />
            </div>
            <p className="text-base font-medium text-slate-600 mb-1">No profiles yet</p>
            <p className="text-sm text-slate-400 mb-4">Create a safety profile to get started</p>
            <button
              {...prefetchHandlers(ROUTES.PROFILES)}
              onClick={() => navigate(ROUTES.PROFILES)}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 font-semibold rounded-xl px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
            >
              <i className="fa-solid fa-plus text-xs" /> Create Profile
            </button>
          </div>
        )}
      </div>

      {/* Footer — fixed on mobile (above FAB), static on desktop */}
      <div className="border-t border-slate-200 mt-8 py-3 text-center">
        <div className="flex items-center justify-center gap-3 text-xs font-semibold text-slate-600">
          <button onClick={() => navigate(ROUTES.TERMS)} className="hover:text-beacon-500 transition-colors">
            <span className="sm:hidden">Terms</span>
            <span className="hidden sm:inline">Terms of Use</span>
          </button>
          <span className="text-slate-300">·</span>
          <button onClick={() => navigate(ROUTES.PRIVACY_POLICY)} className="hover:text-beacon-500 transition-colors">
            <span className="sm:hidden">Privacy</span>
            <span className="hidden sm:inline">Privacy Policy</span>
          </button>
          <span className="text-slate-300">·</span>
          <button onClick={() => navigate(ROUTES.SECURITY_PRIVACY)} className="hover:text-beacon-500 transition-colors">
            <span className="sm:hidden">Security</span>
            <span className="hidden sm:inline">Security & Privacy</span>
          </button>
          <span className="text-slate-300">·</span>
          <button onClick={() => navigate(ROUTES.ABOUT)} className="hover:text-beacon-500 transition-colors">
            <i className="fa-solid fa-shield-heart mr-1" />Transparency
          </button>
        </div>
        <p className="text-xs text-slate-500 font-medium mt-3">Built by a special needs family, for families like ours.</p>
      </div>

    </div>
  )
}
